import { useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormController } from '@/components/ui/form/form-controller.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import {
  type CreateAdditionalCostTypeFormValues,
  schema,
} from '@/dialogs/AccountingAdditionalCostTypeCreateDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { useAccountingAdditionCostTypeMutations } from '@/modules/accounting/hooks/useAccountingAdditionCostTypeMutations.ts'

interface Props extends DialogProps {}

export const AccountingAdditionalCostTypeCreateDialog = ({
  isOpen,
  onClose,
}: Props) => {
  const { createCostType } = useAccountingAdditionCostTypeMutations()

  const form = useForm<CreateAdditionalCostTypeFormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  })

  const handleCreate = useCallback(async () => {
    await createCostType.mutateAsync(form.getValues())

    onClose()
  }, [createCostType, form, onClose])

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <Dialog.Header>
          <Dialog.Title>Create Additional Cost Type</Dialog.Title>
          <Dialog.Description className="text-center">
            <form onSubmit={form.handleSubmit(handleCreate)}>
              <Controller
                name="name"
                render={({ field, fieldState }) => (
                  <FormController>
                    <FormController.Label>Name</FormController.Label>
                    <FormController.Input>
                      <TextField
                        {...field}
                        placeholder="Cost Type Name: e.g. 'Salary'"
                        type="text"
                      />
                    </FormController.Input>
                    {fieldState.error && (
                      <FormController.Error>
                        {fieldState.error.message}
                      </FormController.Error>
                    )}
                  </FormController>
                )}
              />
            </form>
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.ClosableFooter
          className="mt-6"
          onClose={onClose}
        >
          <Button
            isLoading={createCostType.isLoading}
            onClick={handleCreate}
          >
            Create
          </Button>
        </Dialog.ClosableFooter>
      </FormProvider>
    </Dialog>
  )
}
